import { ProblemDetailsError } from '@/api/types'
import type { UserProblem } from '@/types/UserProblem'

/** What a catalogue entry is written as; its key becomes its `code`. */
type ProblemText = Pick<UserProblem, 'summary' | 'fix'>

type ProblemCatalogue<Texts extends Record<string, ProblemText>> = {
  [Code in keyof Texts & string]: Texts[Code] & { code: Code }
}

function defineProblems<Texts extends Record<string, ProblemText>>(
  texts: Texts
): ProblemCatalogue<Texts> {
  const entries = Object.entries(texts).map(([code, text]) => [code, { code, ...text }])
  return Object.fromEntries(entries) as ProblemCatalogue<Texts>
}

/**
 * The problem a failed request reports. The server's own reason is added to the
 * summary when it sent one, since it says which thing went wrong.
 */
function problemFrom<ValueType = unknown>(
  problem: UserProblem<ValueType>,
  cause: unknown
): UserProblem<ValueType> {
  return { ...problem, summary: withReason(problem.summary, cause) }
}

/** As `problemFrom`, but a fix the server phrased replaces the catalogue's (ADR 0034). */
function problemWithServerFix<ValueType = unknown>(
  problem: UserProblem<ValueType>,
  cause: unknown
): UserProblem<ValueType> {
  const serverFix = cause instanceof ProblemDetailsError ? cause.fix : undefined
  return { ...problemFrom(problem, cause), fix: serverFix || problem.fix }
}

function withReason(summary: string, cause: unknown): string {
  const reason = cause instanceof Error ? cause.message.trim() : ''
  return reason ? `${summary}: ${reason}` : summary
}

export { defineProblems, problemFrom, problemWithServerFix }
